import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaRobot, FaHeadset, FaPaperPlane } from "react-icons/fa6";
import TicketService from "../../../../services/Ticket";
import fixDate from "../../../../components/DateFormatter/FixDate";

export default function SupportChat() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const bottomRef = useRef(null);

  const getMessages = useCallback(async () => {
    try {
      setError("");
      const response = await TicketService.getTicketMessages(id);
      setMessages(response?.data?.message || []);
    } catch (err) {
      setError(
        err?.response?.data?.en_message ||
          "Failed to load ticket messages"
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) getMessages();
  }, [id, getMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();

    const description = newMessage.trim();

    if (!description || sending) return;

    try {
      setSending(true);
      setError("");

      await TicketService.userAnswerTicket({
        ticket_id: id,
        description,
      });

      setNewMessage("");
      await getMessages();
    } catch (err) {
      setError(
        err?.response?.data?.en_message ||
          "Failed to send message"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
      <header className="z-10 bg-white px-5 py-4 flex items-center justify-between border-b border-gray-100 shadow-sm">
        <button
          onClick={() => navigate("/support")}
          className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
        >
          <FaArrowLeft className="text-gray-600" />
        </button>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-md">
            <FaHeadset className="text-white" />
          </div>

          <div>
            <h1 className="text-sm font-bold text-gray-900">
              Support Ticket
            </h1>
            <p className="text-xs text-gray-500">Ticket #{id}</p>
          </div>
        </div>

        <div className="w-10" />
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto px-4 py-5 space-y-4">
        {loading ? (
          <div className="flex justify-center py-10">
            <span className="w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          </div>
        ) : error && messages.length === 0 ? (
          <div className="bg-red-50 text-red-600 text-sm rounded-xl p-4 text-center">
            {error}
          </div>
        ) : messages.length > 0 ? (
          messages.map((message) => {
            const isUser = Boolean(message.sender?.user_id);

            return (
              <div
                key={message._id}
                className={`flex items-end gap-2 ${
                  isUser ? "justify-end" : "justify-start"
                }`}
              >
                {!isUser && (
                  <div className="w-8 h-8 bg-gradient-to-r from-primary to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FaRobot className="text-white text-xs" />
                  </div>
                )}

                <div
                  className={`max-w-[78%] flex flex-col ${
                    isUser ? "items-end" : "items-start"
                  }`}
                >
                  <div
                    className={`px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                      isUser
                        ? "bg-gradient-to-r from-primary to-purple-600 text-white rounded-2xl rounded-br-md shadow-md"
                        : "bg-white text-gray-800 rounded-2xl rounded-bl-md border border-gray-100 shadow-sm"
                    }`}
                  >
                    {message.description}
                  </div>

                  <span className="text-[11px] text-gray-400 mt-1 px-1">
                    {fixDate(message.createdAt) || "-"}
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="bg-white rounded-2xl p-6 text-center border border-gray-100">
            <FaHeadset className="text-gray-400 text-3xl mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              No messages in this ticket yet.
            </p>
          </div>
        )}

        <div ref={bottomRef} />
      </main>

      <form
        onSubmit={handleSendMessage}
        className="flex-shrink-0 bg-white border-t border-gray-200 px-4 py-3"
      >
        {error && (
          <p className="text-sm text-red-500 mb-2">{error}</p>
        )}

        <div className="flex items-end gap-3 max-w-4xl mx-auto">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Write your message..."
            rows={1}
            disabled={sending}
            className="flex-1 resize-none rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-primary disabled:bg-gray-100"
          />

          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="w-11 h-11 flex-shrink-0 rounded-full bg-primary text-white flex items-center justify-center hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <FaPaperPlane />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}